// reports/templateClient.ts
//
// Fetches report templates from the backend and caches them by content hash.
//
// The backend stores the templates and their sha256; it never renders a report.
// This client is the other half of that deal: it holds a template until the hash
// moves, revalidates with a few hundred bytes instead of re-downloading tens of
// kilobytes, and — critically — can still produce a report when the network is
// gone, because a cached template is a complete, self-contained document.
//
// Cache key is the kind; the stored hash is what decides freshness. Storing by
// hash-as-key instead would accumulate every historical revision in a storage
// area we share with the rest of the app.

import { API_BASE_URL } from '../api/config';

/** Join the API base with a path.
 *
 * API_BASE_URL already carries the version segment ("…/v1/"), which is why
 * every other call site passes a bare path like "auth/me". Prefixing "/v1/"
 * here produced "…/v1//v1/report-templates/hair" and a 404 on every export. */
function apiUrl(path: string): string {
  return `${API_BASE_URL.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

export type ReportKind = 'base' | 'premium' | 'hair' | 'makeup';

export interface ReportTemplate {
  kind: ReportKind;
  version: string;
  sha256: string;
  html: string;
}

export interface ReportTemplateMeta {
  kind: string;
  version: string;
  sha256: string;
  bytes: number;
  template_url: string;
}

const STORAGE_PREFIX = 'kivo:report-template:';

/** Templates are static assets; a slow fetch must not strand an export. */
const FETCH_TIMEOUT_MS = 10_000;

interface CachedTemplate {
  version: string;
  sha256: string;
  html: string;
}

/** localStorage is absent in some embedded webviews and disabled in private
 *  modes; a missing cache must degrade to a network fetch, never to an error. */
function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function readCache(kind: ReportKind): CachedTemplate | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_PREFIX + kind);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedTemplate>;
    if (typeof parsed.html !== 'string' || typeof parsed.sha256 !== 'string' || parsed.html === '') return null;
    return { version: parsed.version ?? '', sha256: parsed.sha256, html: parsed.html };
  } catch {
    return null;
  }
}

function writeCache(kind: ReportKind, value: CachedTemplate): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_PREFIX + kind, JSON.stringify(value));
  } catch {
    // Quota exhausted or storage disabled. The template is already in hand for
    // this export; losing the cache only costs a re-fetch next time.
  }
}

/** Drop a cached template — used when a cached document fails to render. */
export function clearCachedTemplate(kind: ReportKind): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_PREFIX + kind);
  } catch {
    /* nothing to do: the cache is advisory */
  }
}

async function timedFetch(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => { controller.abort(); }, FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve a template, preferring the cache.
 *
 * The conditional request is what keeps this cheap: a client holding the current
 * revision sends its hash as `If-None-Match` and gets a 304 with no body. Any
 * network failure falls back to the cached copy, so an export works offline
 * once the user has generated a report before.
 */
export async function getReportTemplate(kind: ReportKind): Promise<ReportTemplate> {
  const cached = readCache(kind);
  const url = apiUrl(`report-templates/${kind}`);

  try {
    const res = await timedFetch(url, {
      headers: cached ? { 'If-None-Match': `"${cached.sha256}"` } : {},
    });

    if (res.status === 304 && cached) {
      return { kind, version: cached.version, sha256: cached.sha256, html: cached.html };
    }

    if (res.ok) {
      const html = await res.text();
      const sha256 = (res.headers.get('X-Template-Sha256') ?? stripQuotes(res.headers.get('ETag'))) || '';
      const version = res.headers.get('X-Template-Version') ?? '';
      if (html !== '') {
        writeCache(kind, { version, sha256, html });
        return { kind, version, sha256, html };
      }
    }
  } catch {
    // Offline, aborted, or the backend is down — fall through to the cache.
  }

  if (cached) return { kind, version: cached.version, sha256: cached.sha256, html: cached.html };
  throw new Error(`Report template "${kind}" is unavailable and not cached.`);
}

function stripQuotes(value: string | null): string {
  return value ? value.replace(/^"|"$/g, '') : '';
}

/**
 * Warm the cache for the kinds a user is likely to export.
 *
 * Called on a result screen rather than at export time so the template is
 * already local when the user taps Download — the export then costs no network
 * at all, which is the whole point of moving the render onto the device.
 */
export async function prefetchReportTemplates(kinds: ReportKind[]): Promise<void> {
  // A warm-up is best-effort: one kind failing must not reject the others.
  await Promise.all(kinds.map(k => getReportTemplate(k).catch(() => undefined as ReportTemplate | undefined)));
}

/** Fetch the whole manifest — every kind's hash in one small request. */
export async function getReportTemplateManifest(): Promise<ReportTemplateMeta[]> {
  const res = await timedFetch(apiUrl('report-templates'));
  if (!res.ok) throw new Error(`Report template manifest failed: ${res.status}`);
  const body = (await res.json()) as { templates?: ReportTemplateMeta[] };
  return body.templates ?? [];
}
